/** Vendor tag appended to the shared HBC order-number format (see buildEscriptOrderNumber). */
export const COMPUTASSIST_ORDER_SUFFIX = 'CA';

// ─── Request Payload ─────────────────────────────────────────────────────────

export interface ComputAssistPharmacy {
  ramsno: string; // Pharmacy RAMS/BHF number — used as pharmacy identifier
  storeName?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  postal?: string;
  isFavourite?: boolean;
}

export interface ComputAssistDoctor {
  name: string;
  practicename: string;
  practiceno: string;
  address1?: string;
  address2?: string;
  address3?: string;
  postal?: string;
  phoneno?: string;
  email?: string;
}

export interface ComputAssistMember {
  mainidno: string;
  name: string;
  surname: string;
  title?: string;
  maindob?: string; // Format: YYYYMMDD
  gender?: string;
  language?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  postal?: string;
  email?: string;
  phoneno?: string;
  cellno?: string;
  medaid1?: string; // Primary medical aid scheme code
  medaidno?: string; // Medical aid membership number
  medaid2?: string;
  medaid3?: string;
}

export interface ComputAssistPatient {
  idno: string;
  relation?: string; // "1" = main member, etc.
  name: string;
  surname: string;
  title?: string;
  gender?: string;
  language?: string;
  dob?: string; // Format: YYYYMMDD
  depcode?: string; // Dependant code
}

export interface ComputAssistScriptItem {
  itemno: string; // 1-based item index
  mix: string; // "0" = not a mixture
  napcd: string; // NAPPI code
  descript: string; // Drug description
  qty: string; // Quantity dispensed (e.g. "30.000")
  original_script?: string; // Repeat source script number
  repeats: string;
  supply: string;
  icd10cd?: string;
  directions?: string;
}

export interface ComputAssistScript {
  sctno: string; // Script number (generated by HB)
  medaid?: string; // Primary medical aid scheme code
  payoff?: string;
  scriptdate: string; // Format: "dd mmm yyyy hh:nn:ss" e.g. "05 Nov 2025 14:10:33"
  test: 'Y' | 'N'; // "Y" in dev/QA, "N" in production
  delivery: string; // "0"=Collect "1"=Deliver "2"=COD "3"=Post "4"=Dropbox
  itemcount: string;
  items: ComputAssistScriptItem[];
}

export interface ComputAssistScriptRequest {
  vendorid: string; // Always "hbridge"
  pharmacy: { ramsno: string };
  doctor: ComputAssistDoctor;
  member: ComputAssistMember;
  patient: ComputAssistPatient;
  script: ComputAssistScript;
  message?: string;
  pdf?: string; // Base64-encoded PDF of the script
}

// ─── Submission Response ─────────────────────────────────────────────────────

export interface ComputAssistSubmitResponse {
  code: number; // 0 = success, 1 = failed
  message: string;
  data: null;
}

// ─── Webhook Response (pharmacy → HB after processing) ───────────────────────

export interface ComputAssistAddressResponse {
  type: string;
  line1?: string;
  line2?: string;
  line3?: string;
  line4?: string;
  code?: string;
}

export interface ComputAssistLineResponse {
  no: string;
  nappi: string;
  originalnappi?: string;
  description: string;
  repeatsleft?: string;
  status: string; // "0"=Processed "1"=Incomplete "2"=Rejected "3"=Contact
  claimed?: string;
  copay?: string;
  claimamount?: string; // Actual field name from webhook payload
}

export interface ComputAssistScriptResponse {
  pharmacy: string; // Pharmacy RAMS number from the response
  scriptno: string; // Our sctno, used to look up the Firestore document
  pharmacyscript?: string;
  dateprocessed?: string;
  patientname?: string;
  items?: string;
  status: string; // "0"=Processed "1"=Incomplete "2"=Rejected "3"=Contact
  custommessage?: string;
  claimed?: string;
  copay?: string;
  claim?: string;
}

export interface ComputAssistWebhookResponse {
  response: {
    vendorid: string;
    script: ComputAssistScriptResponse;
    address?: ComputAssistAddressResponse;
    lines?: ComputAssistLineResponse[];
  };
}

// ─── Status ──────────────────────────────────────────────────────────────────

export enum ComputAssistStatusCode {
  /**
   * OUR baseline (not a webhook code): the synchronous send returned code 0 "accepted", which means
   * ComputAssist has RECEIVED the script (same reasoning as Dis-Chem: API-accept = receipt). The
   * webhook later advances to Processed/Incomplete/Rejected/ContactPharmacy.
   */
  ReceivedByPharmacy = -1,
  Processed = 0,
  Incomplete = 1,
  Rejected = 2,
  ContactPharmacy = 3,
}

export const COMPUTASSIST_STATUS_LABELS: Record<ComputAssistStatusCode, string> = {
  [ComputAssistStatusCode.ReceivedByPharmacy]: 'Received by pharmacy',
  [ComputAssistStatusCode.Processed]: 'Script Processed',
  [ComputAssistStatusCode.Incomplete]: 'Incomplete',
  [ComputAssistStatusCode.Rejected]: 'Rejected',
  [ComputAssistStatusCode.ContactPharmacy]: 'Contact Your Pharmacy',
};

// ─── Firestore Document Shape ─────────────────────────────────────────────────

export interface ComputAssistOrderStatusRecord {
  statusCode: number;
  statusUpdatedAt: string;
  statusHistory: Array<{ statusCode: number; timestamp: string }>;
  // Script-level fields (populated by webhook)
  pharmacy?: string; // Pharmacy RAMS number from the response
  pharmacyscript?: string;
  dateprocessed?: string;
  patientname?: string;
  items?: string;
  custommessage?: string;
  // Financial summary (populated by webhook)
  claimed?: string;
  copay?: string;
  claim?: string;
  // Per-item line statuses (populated by webhook)
  lines?: ComputAssistLineResponse[];
}

// ─── Script Request Wrapper ───────────────────────────────────────────────────

export interface ComputAssistSendScriptRequest {
  prescription?: any;
  scriptPdfBase64?: string;
  pharmacy?: ComputAssistPharmacy;
  practiceTenantId?: string;
  encounterId?: string;
  patientId?: string;
}

// ─── Timeline UI ─────────────────────────────────────────────────────────────

export type ComputAssistTimelineStepState = 'completed' | 'active' | 'pending' | 'rejected';

export interface ComputAssistTimelineStep {
  statusCode: number;
  label: string;
  icon: string;
  description: string;
  state: ComputAssistTimelineStepState;
  timestamp?: string;
}

const COMPUTASSIST_STATUS_META: Record<number, { icon: string; description: string }> = {
  [ComputAssistStatusCode.ReceivedByPharmacy]: {
    icon: 'inbox',
    description: 'ComputAssist has received the prescription — awaiting processing by the pharmacy.',
  },
  [ComputAssistStatusCode.Processed]: { icon: 'check_circle', description: 'Script accepted and processed by the pharmacy.' },
  [ComputAssistStatusCode.Incomplete]: {
    icon: 'schedule',
    description: 'Script processing is incomplete. Contact the pharmacy for details.',
  },
  [ComputAssistStatusCode.Rejected]: { icon: 'cancel', description: 'Script was rejected by the pharmacy.' },
  [ComputAssistStatusCode.ContactPharmacy]: { icon: 'phone', description: 'Please contact your pharmacy directly regarding this script.' },
};

/** Synthetic "we submitted" step — not a ComputAssist status code, so it can't collide with 0..3. */
const COMPUTASSIST_SENT_STEP_CODE = -2;

export function buildComputAssistTimelineSteps(statusRecord: ComputAssistOrderStatusRecord): ComputAssistTimelineStep[] {
  const { statusCode: current = ComputAssistStatusCode.ReceivedByPharmacy, statusHistory = [], statusUpdatedAt } = statusRecord;
  const historyMap = new Map(statusHistory.map(h => [h.statusCode, h.timestamp]));

  // Synthetic first step: our submission act, always completed (same pattern as Dis-Chem / EasyRx).
  const sentStep: ComputAssistTimelineStep = {
    statusCode: COMPUTASSIST_SENT_STEP_CODE,
    label: 'Sent to pharmacy',
    icon: 'send',
    description: 'Prescription submitted to ComputAssist.',
    state: 'completed',
  };

  const codes = [...statusHistory.map(h => h.statusCode)];
  if (!historyMap.has(current)) codes.push(current);
  // A successful send = ComputAssist received it: always show the "Received by pharmacy" baseline,
  // even before any webhook advances the order (the streamed doc no longer seeds a status).
  if (!codes.includes(ComputAssistStatusCode.ReceivedByPharmacy)) {
    codes.unshift(ComputAssistStatusCode.ReceivedByPharmacy);
  }

  return [
    sentStep,
    ...codes.map(code => {
      const meta = COMPUTASSIST_STATUS_META[code];
      const isCurrent = code === current;
      const isRejected = code === ComputAssistStatusCode.Rejected;
      const isDone = code === ComputAssistStatusCode.Processed;

      let state: ComputAssistTimelineStepState;
      if (isRejected) {
        state = 'rejected';
      } else if (isCurrent && isDone) {
        state = 'completed';
      } else if (isCurrent) {
        state = 'active';
      } else {
        state = 'completed';
      }

      return {
        statusCode: code,
        label: COMPUTASSIST_STATUS_LABELS[code] ?? `Status ${code}`,
        icon: meta?.icon ?? 'help_outline',
        description: meta?.description ?? '',
        state,
        timestamp: historyMap.get(code) ?? (isCurrent ? statusUpdatedAt : undefined),
      };
    }),
  ];
}
